/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CustomMetricRouteParams } from '../../routes/custom-metrics';
import { MetricGroup } from '../../types/metricGroup';
import { DashboardSection } from '../dashboard';
import { ViewMode } from './constants';
import { CustomMetricContext, getUrls } from './CustomMetricContainer';
import './styles.scss';
import { Menu } from '../menu/Menu';
import { PackAssignmentTable } from './PackAssignmentTable';
import { MetricGroupConfiguration } from './MetricGroupConfiguration';
import { QuestionsAssignmentTable } from './QuestionsAssignmentTable';
import { QUESTION } from '../../constants/terminology';
import { MetricGroupImport } from './MetricGroupImport';
import { useAppSelector } from '../../reducers';
import { isStaff } from '../../selectors/user';
import { FeatureStability } from '@g17eco/molecules';
import { FeatureCode } from '@g17eco/core';

export interface MetricConfig {
  count: number | undefined;
}

export interface FormProps {
  metricGroup?: MetricGroup;
  metricConfig: MetricConfig;
  handleReload: () => Promise<void>;
  handleCancel?: () => void;
}

interface MetricGroupFormProps extends FormProps {
  isAssigned: boolean;
  canEdit: boolean;
  allowCancel?: boolean;
  handleChangeMetricGroup: (params: CustomMetricRouteParams) => void;
}

const MetricGroupForm = (props: MetricGroupFormProps) => {
  const { isAssigned, canEdit, allowCancel, metricGroup, metricConfig, handleReload, handleChangeMetricGroup } = props;
  const { initiativeId, isPortfolioTracker, surveyId, view } = useContext(CustomMetricContext);

  const history = useHistory();
  const isUserStaff = useAppSelector(isStaff);
  const [mode, setMode] = useState<ViewMode>(ViewMode.Edit);
  const urls = getUrls({ isPortfolioTracker, initiativeId, groupId: metricGroup?._id, surveyId });

  useEffect(() => {
    if (view) {
      setMode(view as ViewMode);
    }
  }, [view]);

  const handleCancelImport = () => {
    history.push(urls.assignQuestions);
  };

  const handleCompleteImport = async () => {
    await handleReload();
    handleCancelImport();
  };

  const renderView = (view: ViewMode) => {
    switch (view) {
      case ViewMode.Edit:
        return (
          <MetricGroupConfiguration
            metricGroup={metricGroup}
            handleChangeMetricGroup={handleChangeMetricGroup}
            handleReload={handleReload}
            isAssigned={isAssigned}
            allowCancel={allowCancel}
            editUrl={urls.edit}
          />
        );
      case ViewMode.AssignQuestions:
      case ViewMode.EditQuestion:
        return (
          <QuestionsAssignmentTable
            editQuestionUrl={urls.editQuestion}
            assignQuestionsUrl={urls.assignQuestions}
            canEdit={canEdit}
            isAssigned={isAssigned}
            metricConfig={metricConfig}
            handleReload={handleReload}
            metricGroup={metricGroup}
          />
        );
      case ViewMode.AssignPack:
        return <PackAssignmentTable metricGroup={metricGroup} />;
      case ViewMode.Import:
        if (!metricGroup) {
          return null;
        }
        return <MetricGroupImport
          metricGroup={metricGroup}
          onComplete={handleCompleteImport}
          onCancel={() => handleCancelImport()}
        />
      default:
        return null;
    }
  };

  return (
    <DashboardSection icon='fal fa-file-circle-question' title={metricGroup?.groupName}>
      <Menu
        items={[
          {
            active: mode === ViewMode.Edit,
            onClick: () => history.push(urls.edit),
            label: 'Configuration',
          },
          {
            active: mode === ViewMode.AssignQuestions,
            disabled: !metricGroup?._id,
            onClick: () => history.push(urls.assignQuestions),
            label: `Assign ${QUESTION.PLURAL} to pack`,
          },
          {
            active: mode === ViewMode.AssignPack,
            disabled: !metricGroup?._id,
            onClick: () => history.push(urls.assignPack),
            label: 'Assign pack to companies',
            hidden: !isPortfolioTracker || !metricGroup?._id,
          },
          {
            active: mode === ViewMode.Import,
            disabled: isAssigned,
            onClick: () => history.push(urls.import),
            label: <span>Import / Export <FeatureStability feature={FeatureCode.CustomMetrics} /></span>,
            hidden: !isUserStaff,
          },
        ]}
      />
      {renderView(mode)}
    </DashboardSection>
  );
};
export default MetricGroupForm;
